import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import { edgeErrorMessage } from "@/lib/edge-errors";
import type { EnrichmentRun } from "@/lib/equipment/enrichment";

/** Siste henting for en modell (forslaget som venter, eller den som ble lagret). */
export function useLatestEnrichmentRun(productId: string | null | undefined, enabled = true) {
  return useQuery({
    queryKey: ["equipment-enrichment", productId],
    enabled: enabled && !!productId,
    queryFn: async (): Promise<EnrichmentRun | null> => {
      const { data, error } = await supabase
        .from("equipment_enrichment_runs")
        .select("id, product_id, status, candidates, error, credits_used, created_at")
        .eq("product_id", productId!)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data as EnrichmentRun | null;
    },
  });
}

export class EnrichmentError extends Error {
  readonly outOfCredits: boolean;
  constructor(message: string, outOfCredits: boolean) {
    super(message);
    this.outOfCredits = outOfCredits;
  }
}

/**
 * Henter produktdata for én modell (edge function equipment-enrich). Lagrer bare
 * et forslag; ingenting kopieres før «Lagre valgte». 402 betyr tom for credits.
 */
export function useStartEnrichment() {
  const qc = useQueryClient();
  return useMutation<EnrichmentRun, EnrichmentError, string>({
    mutationFn: async (productId) => {
      const { data, error } = await supabase.functions.invoke<{ run: EnrichmentRun; error?: string }>("equipment-enrich", {
        body: { product_id: productId },
      });
      if (error) {
        const status = (error as { context?: Response }).context?.status;
        throw new EnrichmentError(await edgeErrorMessage(error, data, "Hentingen feilet"), status === 402);
      }
      if (!data?.run) throw new EnrichmentError("Uventet svar fra hentingen", false);
      return data.run;
    },
    onSettled: async (_run, _err, productId) => {
      await invalidateFor(qc, "equipment_enrichment_runs", { productId });
    },
  });
}

export interface ApplyEnrichmentInput {
  runId: string;
  productId: string;
  images: string[];
  documents: string[];
  specs: { label: string; value: string }[] | null;
  description: string | null;
}

export interface ApplyEnrichmentResult {
  results: { url: string; ok: boolean; reason?: string }[];
  saved: number;
  failed: number;
}

/** «Lagre valgte»: kopierer valgte filer til Revehiet (equipment-enrich-apply). Overskriver aldri. */
export function useApplyEnrichment() {
  const qc = useQueryClient();
  return useMutation<ApplyEnrichmentResult, Error, ApplyEnrichmentInput>({
    mutationFn: async (input) => {
      const { data, error } = await supabase.functions.invoke<ApplyEnrichmentResult>("equipment-enrich-apply", {
        body: {
          run_id: input.runId,
          images: input.images,
          documents: input.documents,
          specs: input.specs,
          description: input.description,
        },
      });
      if (error) throw new Error(await edgeErrorMessage(error, null, "Lagringen feilet"));
      if (!data) throw new Error("Uventet svar fra lagringen");
      return data;
    },
    onSettled: async (_res, _err, input) => {
      await invalidateFor(qc, "equipment_enrichment_runs", { productId: input.productId });
      await invalidateFor(qc, "equipment_media", { productId: input.productId });
      await invalidateFor(qc, "equipment_products", { productId: input.productId });
    },
  });
}

/** «Forkast»: forslaget markeres forkastet. Ingenting er lagret fra det. */
export function useDiscardEnrichment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ runId }: { runId: string; productId: string }) => {
      const { error } = await supabase.from("equipment_enrichment_runs").update({ status: "discarded" }).eq("id", runId).eq("status", "ready");
      if (error) throw new Error(`Kunne ikke forkaste forslaget: ${error.message}`);
    },
    onSuccess: async (_res, { productId }) => {
      await invalidateFor(qc, "equipment_enrichment_runs", { productId });
    },
  });
}

/** Gjenværende Firecrawl-credits, vist før «Hent for alle». Leses friskt hver gang siden åpnes. */
export function useFirecrawlCredits(enabled: boolean) {
  return useQuery({
    queryKey: ["equipment-firecrawl-credits"],
    enabled,
    queryFn: async (): Promise<number | null> => {
      const { data, error } = await supabase.functions.invoke<{ remaining_credits: number | null }>("equipment-enrich", {
        body: { action: "credits" },
      });
      if (error) throw new Error(await edgeErrorMessage(error, null, "Kunne ikke lese credits"));
      return data?.remaining_credits ?? null;
    },
    staleTime: 0,
    retry: false,
  });
}
